import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  CloudIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'

import Layout from '../../components/layout/Layout'
import { Badge, Button, Progress, Spinner } from '../../components/ui'
import { cn } from '../../shared/lib/cn'
import { useCloudAccrualRun } from './api'
import { formatCurrency } from './utils'

const navItems = [
  { label: 'Close Review', path: '/cloud-accruals' },
  { label: 'Controller Review', path: '/cloud-accruals/review' },
  { label: 'Source Evidence', path: '/cloud-accruals/sources' },
  { label: 'NetSuite Exports', path: '/cloud-accruals/exports' },
  { label: 'Configuration', path: '/cloud-accruals/configuration' },
]

export default function CloudAccrualsLayout({ children }: { children: ReactNode }) {
  const location = useLocation()
  const runQuery = useCloudAccrualRun({ enabled: location.pathname !== '/cloud-accruals/configuration' })
  const run = runQuery.data

  return (
    <Layout>
      <div className="h-full overflow-auto bg-gray-50 dark:bg-dark-bg">
        <div className="mx-auto flex max-w-[1680px] flex-col gap-6 px-6 py-6">
          <header className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
            <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_420px]">
              <div className="flex min-h-[188px] flex-col justify-between gap-8 p-6 sm:p-7">
                <div className="max-w-3xl">
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <Badge variant="default">EC-05</Badge>
                    <Badge variant="secondary">Cloud usage close</Badge>
                    {run?.summary.hitl_required_count ? (
                      <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                        {run.summary.hitl_required_count} review gates
                      </Badge>
                    ) : null}
                    {run ? (
                      <Badge variant="secondary">Redshift: {run.data_source.redshift_schema}</Badge>
                    ) : null}
                  </div>
                  <h1 className="font-title text-3xl text-charcoal dark:text-gray-100">
                    Cloud Accruals
                  </h1>
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-500 dark:text-gray-400">
                    FinOps-backed month-end accruals for cloud providers, commitments, observability usage, and NetSuite-ready close files.
                  </p>
                </div>

                <nav className="flex flex-wrap items-center gap-2">
                  {navItems.map(item => {
                    const active = location.pathname === item.path
                    return (
                      <Link key={item.path} to={item.path}>
                        <Button
                          variant={active ? 'brand' : 'ghost'}
                          size="sm"
                          className={cn(!active && 'text-gray-600 dark:text-gray-300')}
                        >
                          {item.label}
                        </Button>
                      </Link>
                    )
                  })}
                </nav>
              </div>

              <aside className="border-t border-gray-200 bg-gray-50 p-6 dark:border-dark-border dark:bg-dark-surface-2 lg:border-l lg:border-t-0">
                {runQuery.isLoading ? (
                  <div className="flex h-full min-h-[140px] items-center justify-center">
                    <Spinner size="lg" />
                  </div>
                ) : run ? (
                  <div className="flex h-full flex-col justify-between gap-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          Period
                        </p>
                        <p className="mt-1 text-lg font-semibold text-charcoal dark:text-gray-100">
                          {run.period}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          Entity
                        </p>
                        <p className="mt-1 truncate text-lg font-semibold text-charcoal dark:text-gray-100">
                          {run.entity.replace(', Inc.', '')}
                        </p>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-end justify-between gap-4">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                            Accrual total
                          </p>
                          <p className="mt-1 text-3xl font-semibold text-charcoal dark:text-gray-100">
                            {formatCurrency(run.summary.grand_total)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-300">
                          <ExclamationTriangleIcon className="h-4 w-4" />
                          Review required
                        </div>
                      </div>
                      <Progress
                        value={Math.min((run.summary.grand_total / run.summary.total_threshold) * 100, 100)}
                        className="mt-4 h-2"
                      />
                    </div>

                    <div className="grid gap-2 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                          <CloudIcon className="h-4 w-4" />
                          Usage rows
                        </span>
                        <span className="font-medium text-charcoal dark:text-gray-100">
                          {run.usage_items.length}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                          <ArrowPathIcon className="h-4 w-4" />
                          Reversal
                        </span>
                        <span className="font-medium text-charcoal dark:text-gray-100">
                          {run.exports.reversal.balanced ? 'Balanced' : 'Blocked'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                          <CheckCircleIcon className="h-4 w-4" />
                          Evidence files
                        </span>
                        <span className="font-medium text-charcoal dark:text-gray-100">
                          {run.source_files.length}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    Configure a Redshift data source to load Cloud Accruals.
                  </p>
                )}
              </aside>
            </div>
          </header>

          {children}
        </div>
      </div>
    </Layout>
  )
}
